/**
 * Runs one save per entry, in order, without aborting on failure: a row that fails must not
 * silently drop the rows after it. Each save hook already toasts its own error, so callers only
 * need the partition to decide between advancing and keeping the form open for retry.
 */
export const runSequentialSaves = async <T>(entries: T[], save: (entry: T) => Promise<unknown>): Promise<{ succeeded: T[]; failed: T[] }> => {
  const succeeded: T[] = [];
  const failed: T[] = [];
  for (const entry of entries) {
    try {
      await save(entry);
      succeeded.push(entry);
    } catch {
      failed.push(entry);
    }
  }
  return { succeeded, failed };
};

/**
 * Rows to keep on the form after a partial failure: drops the rows that saved (so a retry can't
 * create duplicates) and pads back to `length` with empty rows.
 */
export const rowsForRetry = <R>(rows: R[], savedRowIndexes: ReadonlySet<number>, length: number, emptyRow: () => R): R[] => {
  const remaining = rows.filter((_, index) => !savedRowIndexes.has(index));
  return [...remaining, ...Array.from({ length: Math.max(0, length - remaining.length) }, emptyRow)];
};
